import React, { useState, useEffect, useCallback } from 'react';
import { adminFetch } from '../../utils/auth';
import { getBackendUrl } from '../../utils/api';
import { formatIST, formatISTDate } from '../../utils/dateFormat';

// Ant Design imports
import {
  Card,
  Typography,
  Button,
  Input,
  Tag,
  Space,
  Alert,
  Modal,
  Drawer,
  Tabs,
  Avatar,
  Tooltip,
  Select,
  Divider,
  Skeleton,
  Row,
  Col,
  Spin,
  Table,
  Form,
  message,
  Flex,
  Grid,
} from 'antd';
import type { TableColumnsType } from 'antd';

// Ant Design Icons
import {
  SearchOutlined,
  PlusOutlined,
  ReloadOutlined,
  EyeOutlined,
  CloseOutlined,
  CheckOutlined,
  CopyOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  BankOutlined,
  MailOutlined,
  PhoneOutlined,
  KeyOutlined,
  HistoryOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  TeamOutlined,
  EditOutlined,
  StopOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';

// Charts
import { SimpleVerticalBarChart } from '../../components/admin/Charts';
import { exportToCSV } from '../../utils/export';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { useBreakpoint } = Grid;

type ClientTabType = 'all' | 'create' | 'pending';

interface ClientManagementProps {
  initialTab?: ClientTabType;
}

interface Client {
  id: number;
  name: string;
  email: string;
  primary_contact: string;
  phone?: string;
  status: string;
  created_at: string;
}

interface ClientAnalytics {
  client_id: number;
  client_name: string;
  client_email: string;
  status: string;
  oauth_client_count: number;
  total_requests: number;
  total_searches: number;
}

interface AuditLog {
  id: number;
  event_type: string;
  user_type: string;
  user_id?: number;
  username?: string;
  resource_type?: string;
  resource_id?: number;
  action: string;
  status: string;
  ip_address?: string;
  error_message?: string;
  request_details?: any;
  response_details?: any;
  created_at?: string;
}

// Scope categories for better organization
const scopeCategories = {
  search: [
    { value: 'search:text', label: 'Text Search', description: 'Allow text-based part searches' },
    { value: 'search:image', label: 'Image Search', description: 'Allow image-based part searches' },
    { value: 'search:hybrid', label: 'Hybrid Search', description: 'Combined text and image search' },
    { value: 'search:asr', label: 'ASR Search', description: 'Audio/speech recognition search' },
  ],
  catalog: [
    { value: 'catalog:read', label: 'Read Catalog', description: 'View catalog data' },
    { value: 'catalog:write', label: 'Write Catalog', description: 'Modify catalog data' },
  ],
  analytics: [
    { value: 'analytics:read', label: 'Read Analytics', description: 'View analytics data' },
    { value: 'analytics:write', label: 'Write Analytics', description: 'Modify analytics' },
  ],
  admin: [
    { value: 'admin:read', label: 'Admin Read', description: 'View admin data' },
    { value: 'admin:write', label: 'Admin Write', description: 'Modify admin data' },
  ],
  other: [
    { value: 'config:read', label: 'Read Config', description: 'View configuration' },
    { value: 'vector:read', label: 'Vector Search', description: 'Access vector search' },
    { value: 'suggestions:read', label: 'Read Suggestions', description: 'Access autocomplete' },
    { value: 'image:upload', label: 'Image Upload', description: 'Upload images for search' },
  ],
};

// Flatten scopes for Select options
const allScopes = Object.entries(scopeCategories).flatMap(([category, scopes]) =>
  scopes.map((scope) => ({ ...scope, category }))
);

// Convert to Select options grouped by category
const scopeSelectOptions = Object.entries(scopeCategories).map(([category, scopes]) => ({
  label: category.charAt(0).toUpperCase() + category.slice(1),
  options: scopes.map((scope) => ({
    label: scope.label,
    value: scope.value,
    description: scope.description,
  })),
}));

// Format entity IDs as PREFIX-NNN (zero-padded) with distinct colors
const fmtId = (prefix: string, id: number | string | null | undefined) =>
  id != null ? `${prefix}-${String(id).padStart(3, '0')}` : null;

const idColors: Record<string, { bg: string; color: string; border: string }> = {
  TNT: { bg: '#e6f4ff', color: '#0958d9', border: '#91caff' },
  BIZ: { bg: '#f6ffed', color: '#389e0d', border: '#b7eb8f' },
  APP: { bg: '#fff7e6', color: '#d46b08', border: '#ffd591' },
};

const idStyle = (prefix: string, size: number = 12) => ({
  fontSize: size,
  fontFamily: "'SF Mono', 'Cascadia Code', 'Consolas', monospace",
  fontWeight: 600 as const,
  color: idColors[prefix]?.color || '#666',
  backgroundColor: idColors[prefix]?.bg || '#f5f5f5',
  border: `1px solid ${idColors[prefix]?.border || '#d9d9d9'}`,
  borderRadius: 4,
  padding: '1px 6px',
  letterSpacing: 0.5,
});

function ClientManagement({ initialTab = 'all' }: ClientManagementProps) {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // State
  const [clients, setClients] = useState<Client[]>([]);
  const [pendingClients, setPendingClients] = useState<any[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // Client details drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clientDetails, setClientDetails] = useState<any>(null);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [drawerTab, setDrawerTab] = useState('overview');
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Create dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [clientForm] = Form.useForm();

  // Businesses state (for drawer)
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [bizLoading, setBizLoading] = useState(false);
  const [createBizModalOpen, setCreateBizModalOpen] = useState(false);
  const [createBizLoading, setCreateBizLoading] = useState(false);
  const [bizForm] = Form.useForm();
  const [selectedBiz, setSelectedBiz] = useState<any>(null);
  const [editBizModalOpen, setEditBizModalOpen] = useState(false);
  const [editBizLoading, setEditBizLoading] = useState(false);
  const [editBizForm] = Form.useForm();

  // OAuth client state (Application credentials)
  const [oauthClients, setOauthClients] = useState<any[]>([]);
  const [oauthClientsLoading, setOauthClientsLoading] = useState(false);
  const [createOAuthLoading, setCreateOAuthLoading] = useState(false);

  // Approval state
  const [approvalComments, setApprovalComments] = useState<{ [clientId: number]: string }>({});
  const [approvingClientId, setApprovingClientId] = useState<number | null>(null);

  // Pending actions state (business/application approval workflow)
  const [pendingActions, setPendingActions] = useState<any[]>([]);
  const [pendingActionsLoading, setPendingActionsLoading] = useState(false);
  const [actionComments, setActionComments] = useState<{ [actionId: number]: string }>({});
  const [resolvingActionId, setResolvingActionId] = useState<number | null>(null);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Data loading functions
  const loadClients = useCallback(async () => {
    try {
      const response = await adminFetch(`${getBackendUrl()}/api/admin/clients?limit=1000`);

      if (response.ok) {
        const data = await response.json();
        setClients(data.data?.clients || []);
        setError('');
      } else if (response.status === 401) {
        setError('Authentication failed. Please check your token.');
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        setError(`Failed to load clients: ${errorData.message || 'Unknown error'}`);
      }
    } catch (err: any) {
      setError(`Error loading clients: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPendingClients = useCallback(async () => {
    try {
      const response = await adminFetch(`${getBackendUrl()}/api/tenant/pending?limit=100`);

      if (response.ok) {
        const data = await response.json();
        setPendingClients(data.data?.clients || []);
      }
    } catch (err: any) {
      console.error('Error loading pending clients:', err);
    }
  }, []);

  const loadPendingActions = useCallback(async () => {
    try {
      setPendingActionsLoading(true);
      const response = await adminFetch(`${getBackendUrl()}/api/admin/pending-actions?status=pending&limit=200`);

      if (response.ok) {
        const data = await response.json();
        setPendingActions(data.actions || []);
      }
    } catch (err: any) {
      console.error('Error loading pending actions:', err);
    } finally {
      setPendingActionsLoading(false);
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      const response = await adminFetch(`${getBackendUrl()}/api/admin/clients/analytics`);

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data.data);
      }
    } catch (err: any) {
      // Silently fail analytics loading
    }
  }, []);

  const loadClientDetails = useCallback(async (clientId: number) => {
    setDetailsLoading(true);
    try {
      const [detailsRes, auditRes] = await Promise.all([
        adminFetch(`${getBackendUrl()}/api/admin/clients/${clientId}/stats`),
        adminFetch(`${getBackendUrl()}/api/admin/audit-logs?resource_type=tenant&resource_id=${clientId}&limit=50`),
      ]);

      if (detailsRes.ok) {
        const detailsData = await detailsRes.json();
        setClientDetails(detailsData.data);
      }

      if (auditRes.ok) {
        const auditData = await auditRes.json();
        setAuditLogs(auditData.data?.logs || []);
      }
    } catch (err: any) {
      console.error('Error loading client details:', err);
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  // Load businesses for a tenant
  const loadBusinesses = useCallback(async (tenantId: number) => {
    setBizLoading(true);
    try {
      const response = await adminFetch(`${getBackendUrl()}/api/admin/tenants/${tenantId}/businesses`);
      if (response.ok) {
        const data = await response.json();
        setBusinesses(data.businesses || data.data?.businesses || []);
      }
    } catch (err: any) {
      console.error('Error loading businesses:', err);
    } finally {
      setBizLoading(false);
    }
  }, []);

  // OAuth client management (Application credentials)
  const loadOAuthClients = useCallback(async (tenantId: number, bizId: number) => {
    setOauthClientsLoading(true);
    try {
      const response = await adminFetch(`${getBackendUrl()}/api/auth/oauth/clients?tenant_id=${tenantId}&business_id=${bizId}`);
      if (response.ok) {
        const data = await response.json();
        setOauthClients(Array.isArray(data.data) ? data.data : data.data?.clients || data.clients || []);
      }
    } catch (err: any) {
      console.error('Error loading OAuth clients:', err);
    } finally {
      setOauthClientsLoading(false);
    }
  }, []);

  const handleCreateOAuthClient = async () => {
    if (!selectedClient || !selectedBiz) return;
    setCreateOAuthLoading(true);
    try {
      const response = await adminFetch(
        `${getBackendUrl()}/api/auth/oauth/clients`,
        {
          method: 'POST',
          body: JSON.stringify({
            business_id: selectedBiz.id,
            tenant_id: selectedClient.id,
            scopes: selectedBiz.scopes || [],
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        const clientData = data.data || data;
        await loadOAuthClients(selectedClient.id, selectedBiz.id);
        Modal.success({
          title: 'Application Created',
          content: (
            <div>
              <Alert
                message="Save the Client Secret now. It will only be shown once."
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
              />
              <Text strong style={{ display: 'block', marginBottom: 4 }}>Client ID:</Text>
              <Input.TextArea
                value={clientData.client_id}
                readOnly
                autoSize={{ minRows: 1 }}
                style={{ marginBottom: 8, fontFamily: 'monospace', fontSize: 13 }}
              />
              <Button
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(clientData.client_id)}
                style={{ marginBottom: 12 }}
                block
              >
                Copy Client ID
              </Button>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>Client Secret:</Text>
              <Input.TextArea
                value={clientData.client_secret}
                readOnly
                autoSize={{ minRows: 1 }}
                style={{ marginBottom: 8, fontFamily: 'monospace', fontSize: 13 }}
              />
              <Button
                type="primary"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(clientData.client_secret)}
                block
              >
                Copy Client Secret
              </Button>
            </div>
          ),
          width: 520,
        });
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        message.error(errorData.detail || errorData.message || 'Failed to create application');
      }
    } catch (err: any) {
      message.error(`Error: ${err.message}`);
    } finally {
      setCreateOAuthLoading(false);
    }
  };

  const handleRevokeOAuthClient = async (clientId: string) => {
    if (!selectedClient || !selectedBiz) return;
    try {
      const response = await adminFetch(
        `${getBackendUrl()}/api/auth/oauth/clients/${clientId}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        message.success('Application revoked');
        await loadOAuthClients(selectedClient.id, selectedBiz.id);
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        message.error(errorData.detail || 'Failed to revoke application');
      }
    } catch (err: any) {
      message.error(`Error: ${err.message}`);
    }
  };

  const handleRotateSecret = async (clientId: string) => {
    if (!selectedClient || !selectedBiz) return;
    try {
      const response = await adminFetch(
        `${getBackendUrl()}/api/auth/oauth/clients/${clientId}/rotate-secret`,
        { method: 'POST' }
      );

      if (response.ok) {
        const data = await response.json();
        const clientData = data.data || data;
        await loadOAuthClients(selectedClient.id, selectedBiz.id);
        Modal.success({
          title: 'Client Secret Rotated',
          content: (
            <div>
              <Alert message="The old secret has been invalidated." type="warning" showIcon style={{ marginBottom: 12 }} />
              <Text strong style={{ display: 'block', marginBottom: 4 }}>New Client Secret:</Text>
              <Input.TextArea
                value={clientData.client_secret}
                readOnly
                autoSize={{ minRows: 1 }}
                style={{ marginBottom: 8, fontFamily: 'monospace', fontSize: 13 }}
              />
              <Button
                type="primary"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(clientData.client_secret)}
                block
              >
                Copy New Secret
              </Button>
            </div>
          ),
          width: 500,
        });
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        message.error(errorData.detail || 'Failed to rotate secret');
      }
    } catch (err: any) {
      message.error(`Error: ${err.message}`);
    }
  };

  // Initial load and refresh interval
  useEffect(() => {
    loadClients();
    loadPendingClients();
    loadPendingActions();
    loadAnalytics();
    const interval = setInterval(() => {
      loadClients();
      loadPendingClients();
      loadPendingActions();
      loadAnalytics();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadClients, loadPendingClients, loadPendingActions, loadAnalytics]);

  // Load client details when drawer opens
  useEffect(() => {
    if (selectedClient && drawerOpen) {
      loadClientDetails(selectedClient.id);
      loadBusinesses(selectedClient.id);
    }
  }, [selectedClient, drawerOpen, loadClientDetails, loadBusinesses]);

  // Handlers
  const handleRefresh = () => {
    loadClients();
    loadPendingClients();
    loadPendingActions();
    loadAnalytics();
  };

  const handleOpenDrawer = (client: Client) => {
    setSelectedClient(client);
    setDrawerTab('overview');
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedClient(null);
    setClientDetails(null);
    setAuditLogs([]);
    setBusinesses([]);
    setSelectedBiz(null);
  };

  // Business handlers
  const handleCreateBusiness = async () => {
    try {
      const values = await bizForm.validateFields();
      if (!selectedClient) return;

      setCreateBizLoading(true);

      const payload: any = {
        name: values.biz_name.trim(),
        description: values.biz_description?.trim() || '',
        scopes: values.biz_scopes || [],
        rate_limit_per_hour: values.rate_limit_per_hour || 1000,
        rate_limit_per_minute: values.rate_limit_per_minute || 60,
      };

      if (values.daily_quota) payload.daily_quota = parseInt(values.daily_quota);
      if (values.monthly_quota) payload.monthly_quota = parseInt(values.monthly_quota);

      const response = await adminFetch(`${getBackendUrl()}/api/admin/tenants/${selectedClient.id}/businesses`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (response.status === 202) {
        bizForm.resetFields();
        setCreateBizModalOpen(false);
        await loadPendingActions();
        message.info('Business creation submitted for approval');
      } else if (response.ok) {
        bizForm.resetFields();
        setCreateBizModalOpen(false);
        await loadBusinesses(selectedClient.id);
        message.success('Business created successfully');
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        message.error(errorData.message || errorData.detail || 'Failed to create business');
      }
    } catch (err: any) {
      if (err.errorFields) return;
      message.error(`Error: ${err.message || 'Failed to create business'}`);
    } finally {
      setCreateBizLoading(false);
    }
  };

  const handleEditBusiness = async () => {
    try {
      const values = await editBizForm.validateFields();
      if (!selectedClient || !selectedBiz) return;

      setEditBizLoading(true);

      const payload: any = {
        name: values.biz_name.trim(),
        description: values.biz_description?.trim() || '',
        scopes: values.biz_scopes || [],
        rate_limit_per_hour: values.rate_limit_per_hour || 1000,
        rate_limit_per_minute: values.rate_limit_per_minute || 60,
      };

      if (values.daily_quota) payload.daily_quota = parseInt(values.daily_quota);
      else payload.daily_quota = null;
      if (values.monthly_quota) payload.monthly_quota = parseInt(values.monthly_quota);
      else payload.monthly_quota = null;

      const response = await adminFetch(`${getBackendUrl()}/api/admin/tenants/${selectedClient.id}/businesses/${selectedBiz.id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const updatedBiz = await response.json();
        setEditBizModalOpen(false);
        setSelectedBiz(updatedBiz);
        await loadBusinesses(selectedClient.id);
        message.success('Business updated successfully');
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        message.error(errorData.message || errorData.detail || 'Failed to update business');
      }
    } catch (err: any) {
      if (err.errorFields) return;
      message.error(`Error: ${err.message || 'Failed to update business'}`);
    } finally {
      setEditBizLoading(false);
    }
  };

  const openEditBizModal = (biz: any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setSelectedBiz(biz);
    editBizForm.setFieldsValue({
      biz_name: biz.name,
      biz_description: biz.description,
      biz_scopes: biz.scopes || [],
      rate_limit_per_hour: biz.rate_limit_per_hour || 1000,
      rate_limit_per_minute: biz.rate_limit_per_minute || 60,
      daily_quota: biz.daily_quota || undefined,
      monthly_quota: biz.monthly_quota || undefined,
    });
    setEditBizModalOpen(true);
  };

  const handleSelectBiz = (biz: any) => {
    setSelectedBiz(biz);
    if (selectedClient) {
      loadOAuthClients(selectedClient.id, biz.id);
    }
  };

  const handleApproveClient = async (clientId: number, status: 'approved' | 'rejected') => {
    try {
      setApprovingClientId(clientId);

      const response = await adminFetch(`${getBackendUrl()}/api/tenant/${clientId}/approve`, {
        method: 'POST',
        body: JSON.stringify({
          status,
          comments: approvalComments[clientId] || undefined,
        }),
      });

      if (response.ok) {
        setApprovalComments((prev) => ({ ...prev, [clientId]: '' }));
        await loadClients();
        await loadPendingClients();
        if (selectedClient?.id === clientId) {
          await loadClientDetails(clientId);
        }
        message.success(`Tenant ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to update client status' }));
        message.error(errorData.detail || errorData.message || 'Failed to update client status');
      }
    } catch (err: any) {
      message.error(`Error: ${err.message}`);
    } finally {
      setApprovingClientId(null);
    }
  };

  const handleResolvePendingAction = async (actionId: number, resolution: 'approve' | 'reject') => {
    try {
      setResolvingActionId(actionId);

      const response = await adminFetch(`${getBackendUrl()}/api/admin/pending-actions/${actionId}/${resolution}`, {
        method: 'POST',
        body: JSON.stringify({ comments: actionComments[actionId] || undefined }),
      });

      if (response.ok) {
        const data = await response.json();
        setActionComments((prev) => ({ ...prev, [actionId]: '' }));
        await loadPendingActions();

        message.success(`Action ${resolution === 'approve' ? 'approved' : 'rejected'} successfully`);

        // Refresh business lists if drawer is open
        if (selectedClient) {
          await loadBusinesses(selectedClient.id);
        }
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed' }));
        message.error(errorData.detail || errorData.message || 'Failed to resolve action');
      }
    } catch (err: any) {
      message.error(`Error: ${err.message}`);
    } finally {
      setResolvingActionId(null);
    }
  };

  const handleSuspendTenant = async (tenantId: number) => {
    Modal.confirm({
      title: 'Suspend Tenant',
      icon: <ExclamationCircleOutlined />,
      content: 'This will deactivate all businesses and applications under this tenant, and cancel all pending actions. This action cannot be easily undone.',
      okText: 'Suspend',
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await adminFetch(`${getBackendUrl()}/api/tenant/${tenantId}/suspend`, {
            method: 'POST',
            body: JSON.stringify({}),
          });

          if (response.ok) {
            message.success('Tenant suspended successfully');
            await loadClients();
            await loadPendingActions();
            if (selectedClient?.id === tenantId) {
              await loadClientDetails(tenantId);
              await loadBusinesses(tenantId);
            }
          } else {
            const errorData = await response.json().catch(() => ({ detail: 'Failed' }));
            message.error(errorData.detail || 'Failed to suspend tenant');
          }
        } catch (err: any) {
          message.error(`Error: ${err.message}`);
        }
      },
    });
  };

  const handleCreateClient = async () => {
    try {
      const values = await clientForm.validateFields();
      setCreateLoading(true);

      const payload: any = {
        name: values.name.trim(),
        email: values.email.trim(),
      };

      if (values.primary_contact?.trim()) payload.primary_contact = values.primary_contact.trim();
      if (values.phone?.trim()) payload.phone = values.phone.trim();

      const response = await adminFetch(`${getBackendUrl()}/api/admin/clients`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        clientForm.resetFields();
        setCreateOpen(false);
        await loadClients();
        await loadAnalytics();
        message.success('Tenant created successfully. Add Businesses and Applications in the tenant drawer.');
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        message.error(errorData.message || errorData.detail || 'Failed to create tenant');
      }
    } catch (err: any) {
      if (err.errorFields) return;
      message.error(`Error: ${err.message || 'Failed to create tenant'}`);
    } finally {
      setCreateLoading(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success('Copied to clipboard');
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      message.success('Copied to clipboard');
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string): React.ReactElement | undefined => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return <CheckCircleOutlined />;
      case 'pending':
        return <ClockCircleOutlined />;
      case 'rejected':
        return <CloseCircleOutlined />;
      default:
        return undefined;
    }
  };

  // Filter clients based on search and status
  const filteredClients = clients.filter((client) => {
    const matchesSearch =
      !searchTerm ||
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.primary_contact?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = !statusFilter || client.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Table columns with tooltips for non-technical users
  const columns: TableColumnsType<Client> = [
    {
      title: (
        <Tooltip title="Unique identifier for each tenant">
          <span>ID</span>
        </Tooltip>
      ),
      dataIndex: 'id',
      key: 'id',
      width: 100,
      sorter: (a, b) => a.id - b.id,
      render: (value: number) => (
        <span style={idStyle('TNT', 12)}>{fmtId('TNT', value)}</span>
      ),
    },
    {
      title: (
        <Tooltip title="Tenant's registered name">
          <span>Name</span>
        </Tooltip>
      ),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (value: string) => (
        <Space>
          <Avatar size={32} style={{ backgroundColor: '#1677ff', fontSize: '0.875rem' }}>
            {value?.[0]?.toUpperCase()}
          </Avatar>
          <Text strong>{value}</Text>
        </Space>
      ),
    },
    {
      title: (
        <Tooltip title="Contact email address">
          <span>Email</span>
        </Tooltip>
      ),
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: (
        <Tooltip title="Primary contact person for this tenant">
          <span>Primary Contact</span>
        </Tooltip>
      ),
      dataIndex: 'primary_contact',
      key: 'primary_contact',
      sorter: (a, b) => (a.primary_contact || '').localeCompare(b.primary_contact || ''),
      render: (value: string) => value || '-',
    },
    {
      title: (
        <Tooltip title="Approved: Active access | Pending: Awaiting review | Rejected: Access denied">
          <span>Status</span>
        </Tooltip>
      ),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      filters: [
        { text: 'Approved', value: 'approved' },
        { text: 'Pending', value: 'pending' },
        { text: 'Rejected', value: 'rejected' },
      ],
      onFilter: (value, record) => record.status === value,
      render: (value: string) => (
        <Tag color={getStatusColor(value)} icon={getStatusIcon(value)}>
          {value}
        </Tag>
      ),
    },
    {
      title: (
        <Tooltip title="Date when the tenant was registered">
          <span>Created</span>
        </Tooltip>
      ),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (value: string) => (value ? formatISTDate(value) : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record: Client) => (
        <Tooltip title="View tenant details, businesses, and approval history">
          <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => handleOpenDrawer(record)} />
        </Tooltip>
      ),
    },
  ];

  // Tenant usage data for chart
  const clientUsageData =
    analytics?.clients?.slice(0, 10).map((client: ClientAnalytics) => ({
      name: client.client_name || `Tenant ${client.client_id}`,
      requests: client.total_requests || 0,
    })) || [];

  // Loading state
  if (loading && clients.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Skeleton active paragraph={{ rows: 1 }} />
          <Row gutter={[24, 24]}>
            {[1, 2, 3, 4].map((i) => (
              <Col xs={24} sm={12} md={6} key={i}>
                <Skeleton active paragraph={{ rows: 2 }} />
              </Col>
            ))}
          </Row>
          <Skeleton active paragraph={{ rows: 10 }} />
        </Space>
      </div>
    );
  }

  // Render All Clients content
  const renderAllClientsContent = () => (
    <>
      {/* Stats Cards */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Flex justify="space-between" align="flex-start">
              <div>
                <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Total Tenants
                </Text>
                <Title level={2} style={{ margin: 0, fontWeight: 700 }}>
                  {clients.length}
                </Title>
              </div>
              <Avatar style={{ backgroundColor: '#e6f4ff' }} icon={<TeamOutlined style={{ color: '#1677ff' }} />} />
            </Flex>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Flex justify="space-between" align="flex-start">
              <div>
                <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Approved
                </Text>
                <Title level={2} style={{ margin: 0, fontWeight: 700, color: '#52c41a' }}>
                  {clients.filter((c) => c.status === 'approved').length}
                </Title>
              </div>
              <Avatar style={{ backgroundColor: '#f6ffed' }} icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />} />
            </Flex>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Flex justify="space-between" align="flex-start">
              <div>
                <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Pending
                </Text>
                <Title level={2} style={{ margin: 0, fontWeight: 700, color: '#faad14' }}>
                  {pendingClients.length}
                </Title>
              </div>
              <Avatar style={{ backgroundColor: '#fffbe6' }} icon={<ClockCircleOutlined style={{ color: '#faad14' }} />} />
            </Flex>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Flex justify="space-between" align="flex-start">
              <div>
                <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Rejected
                </Text>
                <Title level={2} style={{ margin: 0, fontWeight: 700, color: '#ff4d4f' }}>
                  {clients.filter((c) => c.status === 'rejected').length}
                </Title>
              </div>
              <Avatar style={{ backgroundColor: '#fff2f0' }} icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />} />
            </Flex>
          </Card>
        </Col>
      </Row>

      {/* Tenant Usage Chart */}
      {clientUsageData.length > 0 && (
        <Card style={{ marginBottom: 24 }}>
          <SimpleVerticalBarChart data={clientUsageData} dataKey="requests" title="Top Tenants by API Usage" height={300} />
        </Card>
      )}

      {/* Tenants Table */}
      <Card>
        {/* Toolbar */}
        <Flex wrap="wrap" gap={16} style={{ marginBottom: 16 }} align="center">
          <Input
            placeholder="Search tenants..."
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 250 }}
            allowClear
          />
          <Select
            placeholder="Filter by Status"
            value={statusFilter || undefined}
            onChange={(value) => setStatusFilter(value || '')}
            allowClear
            style={{ width: 150 }}
            options={[
              { label: 'All', value: '' },
              { label: 'Approved', value: 'approved' },
              { label: 'Pending', value: 'pending' },
              { label: 'Rejected', value: 'rejected' },
            ]}
          />
          <div style={{ flex: 1 }} />
          <Tooltip title="Download the current tenant list as a CSV file">
            <Button icon={<DownloadOutlined />} onClick={() => exportToCSV(filteredClients, 'tenants')}>
              Export CSV
            </Button>
          </Tooltip>
          <Tooltip title="Refresh tenant list">
            <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
              {loading ? 'Loading...' : 'Refresh'}
            </Button>
          </Tooltip>
        </Flex>

        {/* Table */}
        <Table
          dataSource={filteredClients}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            defaultPageSize: 25,
            pageSizeOptions: ['10', '25', '50', '100'],
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} tenants`,
          }}
          scroll={{ y: 500 }}
          size="middle"
        />
      </Card>
    </>
  );

  // Render Pending Approvals content
  const actionTypeLabels: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
    create_business: { label: 'Create Business', color: 'blue', icon: <AppstoreOutlined /> },
    delete_business: { label: 'Delete Business', color: 'red', icon: <DeleteOutlined /> },
    create_application: { label: 'Create Application', color: 'cyan', icon: <AppstoreOutlined /> },
    delete_application: { label: 'Delete Application', color: 'red', icon: <DeleteOutlined /> },
  };

  const renderPendingApprovalsContent = () => {
    const totalPending = pendingClients.length + pendingActions.length;
    return (
      <>
        <Alert
          type="info"
          showIcon
          message="Approval Workflow"
          description="Review pending tenant registrations, business, and application actions below. Approved items are executed immediately."
          style={{ marginBottom: 16 }}
        />

        {totalPending === 0 ? (
          <Card>
            <Alert message="No pending approvals - all caught up!" type="success" showIcon />
          </Card>
        ) : (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Section 1: Tenant Registrations */}
            {pendingClients.length > 0 && (
              <>
                <Text strong style={{ fontSize: 14 }}>
                  <TeamOutlined style={{ marginRight: 6 }} />
                  Tenant Registrations ({pendingClients.length})
                </Text>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {pendingClients.map((client) => (
                    <Card key={`tenant-${client.id}`} size="small">
                      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={16}>
                        <Flex gap={12} align="flex-start">
                          <Avatar style={{ backgroundColor: '#faad14' }}>{client.name?.[0]?.toUpperCase()}</Avatar>
                          <div>
                            <Flex align="center" gap={6}>
                              <Text strong>{client.name}</Text>
                              <span style={idStyle('TNT', 11)}>{fmtId('TNT', client.id)}</span>
                            </Flex>
                            <Text type="secondary">{client.email}</Text>
                          </div>
                        </Flex>
                        <Space>
                          {client.workflow?.priority && (
                            <Tag
                              color={
                                client.workflow.priority === 'urgent'
                                  ? 'error'
                                  : client.workflow.priority === 'high'
                                  ? 'warning'
                                  : 'blue'
                              }
                              icon={client.workflow.priority === 'urgent' ? <ExclamationCircleOutlined /> : undefined}
                            >
                              {client.workflow.priority}
                            </Tag>
                          )}
                          <Tag color={client.workflow?.days_pending > 14 ? 'error' : 'default'}>
                            {client.workflow?.days_pending || 0} days
                          </Tag>
                        </Space>
                      </Flex>

                      <Row gutter={[16, 8]} style={{ marginTop: 16, marginBottom: 16 }}>
                        <Col xs={12} sm={6}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Primary Contact</Text>
                          <div>{client.primary_contact || 'N/A'}</div>
                        </Col>
                        <Col xs={12} sm={6}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Phone</Text>
                          <div>{client.phone || 'N/A'}</div>
                        </Col>
                        <Col xs={12} sm={6}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Submitted</Text>
                          <div>
                            {client.workflow?.submitted_at
                              ? formatISTDate(client.workflow.submitted_at)
                              : client.created_at
                              ? formatISTDate(client.created_at)
                              : 'N/A'}
                          </div>
                        </Col>
                        <Col xs={12} sm={6}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Status</Text>
                          <div>
                            <Tag color={getStatusColor(client.status)}>{client.status}</Tag>
                          </div>
                        </Col>
                      </Row>

                      <TextArea
                        placeholder="Add approval comment (optional)..."
                        rows={2}
                        value={approvalComments[client.id] || ''}
                        onChange={(e) => setApprovalComments((prev) => ({ ...prev, [client.id]: e.target.value }))}
                      />

                      <Flex justify="flex-end" gap={8} style={{ marginTop: 16 }}>
                        <Button
                          danger
                          icon={<CloseOutlined />}
                          onClick={() => handleApproveClient(client.id, 'rejected')}
                          disabled={approvingClientId === client.id}
                        >
                          Reject
                        </Button>
                        <Button
                          type="primary"
                          icon={<CheckOutlined />}
                          onClick={() => handleApproveClient(client.id, 'approved')}
                          loading={approvingClientId === client.id}
                          style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                        >
                          Approve
                        </Button>
                      </Flex>
                    </Card>
                  ))}
                </Space>
              </>
            )}

            {/* Section 2: Business & Application Actions */}
            {pendingActions.length > 0 && (
              <>
                {pendingClients.length > 0 && <Divider />}
                <Text strong style={{ fontSize: 14 }}>
                  <AppstoreOutlined style={{ marginRight: 6 }} />
                  Business & Application Actions ({pendingActions.length})
                </Text>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {pendingActions.map((action) => {
                    const typeInfo = actionTypeLabels[action.action_type] || { label: action.action_type, color: 'default', icon: null };
                    return (
                      <Card key={`action-${action.id}`} size="small">
                        <Flex justify="space-between" align="flex-start" wrap="wrap" gap={12}>
                          <Flex gap={12} align="flex-start">
                            <Avatar style={{ backgroundColor: typeInfo.color === 'red' ? '#ff4d4f' : typeInfo.color === 'blue' ? '#1677ff' : typeInfo.color === 'cyan' ? '#13c2c2' : '#fa8c16' }}>
                              {typeInfo.icon}
                            </Avatar>
                            <div>
                              <Flex align="center" gap={6} wrap="wrap">
                                <Tag color={typeInfo.color}>{typeInfo.label}</Tag>
                                {action.tenant_name && (
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    Tenant: <Text strong style={{ fontSize: 12 }}>{action.tenant_name}</Text>
                                  </Text>
                                )}
                                {action.business_name && (
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    Business: <Text strong style={{ fontSize: 12 }}>{action.business_name}</Text>
                                  </Text>
                                )}
                              </Flex>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                Submitted by {action.requested_by_username || 'unknown'}{' '}
                                {action.submitted_at ? `on ${formatISTDate(action.submitted_at)}` : ''}
                              </Text>
                            </div>
                          </Flex>
                        </Flex>

                        {/* Payload summary */}
                        <div style={{ marginTop: 12, marginBottom: 12, padding: '8px 12px', background: '#f5f5f5', borderRadius: 6, fontSize: 13 }}>
                          {action.payload?.name && <div><Text type="secondary">Name:</Text> {action.payload.name}</div>}
                          {action.payload?.username && <div><Text type="secondary">Application Name:</Text> {action.payload.username}</div>}
                          {action.payload?.scopes && (
                            <Flex wrap="wrap" gap={4} style={{ marginTop: 4 }}>
                              {action.payload.scopes.map((s: string) => (
                                <Tag key={s} style={{ fontSize: 11 }}>{s}</Tag>
                              ))}
                            </Flex>
                          )}
                          {action.comments && <div style={{ marginTop: 4 }}><Text type="secondary">Comment:</Text> {action.comments}</div>}
                        </div>

                        <TextArea
                          placeholder="Add resolution comment (optional)..."
                          rows={2}
                          value={actionComments[action.id] || ''}
                          onChange={(e) => setActionComments((prev) => ({ ...prev, [action.id]: e.target.value }))}
                        />

                        <Flex justify="flex-end" gap={8} style={{ marginTop: 12 }}>
                          <Button
                            danger
                            icon={<CloseOutlined />}
                            onClick={() => handleResolvePendingAction(action.id, 'reject')}
                            disabled={resolvingActionId === action.id}
                          >
                            Reject
                          </Button>
                          <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            onClick={() => handleResolvePendingAction(action.id, 'approve')}
                            loading={resolvingActionId === action.id}
                            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                          >
                            Approve
                          </Button>
                        </Flex>
                      </Card>
                    );
                  })}
                </Space>
              </>
            )}
          </Space>
        )}
      </>
    );
  };

  // Render Create Tenant form content (inline, simplified -- basic info only)
  const renderCreateClientContent = () => (
    <Card>
      <Alert
        type="info"
        showIcon
        message="Simplified Tenant Creation"
        description="Create the tenant with basic details. Scopes, rate limits, and applications are now managed per-Business inside the tenant drawer."
        style={{ marginBottom: 24 }}
      />

      <Form form={clientForm} layout="vertical">
        <Form.Item name="name" label="Tenant Name" tooltip="The display name for this tenant" rules={[{ required: true, message: 'Name is required' }]}>
          <Input prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} placeholder="Enter tenant name" />
        </Form.Item>
        <Form.Item name="email" label="Email" tooltip="Primary contact email" rules={[{ required: true, message: 'Email is required' }, { type: 'email', message: 'Invalid email format' }]}>
          <Input prefix={<MailOutlined style={{ color: '#bfbfbf' }} />} placeholder="Enter email" />
        </Form.Item>
        <Form.Item name="primary_contact" label="Primary Contact" tooltip="Primary contact person name (optional)">
          <Input prefix={<BankOutlined style={{ color: '#bfbfbf' }} />} placeholder="Enter primary contact name" />
        </Form.Item>
        <Form.Item name="phone" label="Phone" tooltip="Contact phone number (optional)">
          <Input prefix={<PhoneOutlined style={{ color: '#bfbfbf' }} />} placeholder="Enter phone number" />
        </Form.Item>
      </Form>

      <Flex justify="flex-end" style={{ marginTop: 24 }}>
        <Space>
          <Button onClick={() => clientForm.resetFields()}>Reset</Button>
          <Button type="primary" onClick={handleCreateClient} loading={createLoading}>
            Create Tenant
          </Button>
        </Space>
      </Flex>
    </Card>
  );

  // Render content based on initialTab
  const renderContent = () => {
    switch (initialTab) {
      case 'all':
        return renderAllClientsContent();
      case 'create':
        return renderCreateClientContent();
      case 'pending':
        return renderPendingApprovalsContent();
      default:
        return renderAllClientsContent();
    }
  };

  const drawerTabItems = [
    {
      key: 'overview',
      label: (
        <Space>
          <UserOutlined />
          Overview
        </Space>
      ),
      children: detailsLoading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : clientDetails ? (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Flex justify="space-between" align="center">
            <Text strong>Overview</Text>
            <Button
              size="small"
              type="text"
              icon={<ReloadOutlined />}
              loading={detailsLoading}
              onClick={() => { if (selectedClient) { loadClientDetails(selectedClient.id); loadBusinesses(selectedClient.id); } }}
            />
          </Flex>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Card size="small">
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
                  Tenant ID
                </Text>
                <div style={{ marginTop: 4 }}>
                  <span style={idStyle('TNT', 14)}>{fmtId('TNT', clientDetails.client_id)}</span>
                </div>
              </Card>
            </Col>
            <Col span={12}>
              <Card size="small">
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
                  Businesses
                </Text>
                <Title level={4} style={{ margin: 0 }}>
                  {businesses.length}
                </Title>
              </Card>
            </Col>
            <Col span={12}>
              <Card size="small">
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
                  Total Requests
                </Text>
                <Title level={4} style={{ margin: 0 }}>
                  {(clientDetails.stats?.total_requests || 0).toLocaleString()}
                </Title>
              </Card>
            </Col>
            <Col span={12}>
              <Card size="small">
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
                  Total Searches
                </Text>
                <Title level={4} style={{ margin: 0 }}>
                  {(clientDetails.stats?.total_searches || 0).toLocaleString()}
                </Title>
              </Card>
            </Col>
          </Row>

          <Divider style={{ margin: '8px 0' }} />

          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Tenant Information
            </Text>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 12 }}>Name</Text>
                <div>{clientDetails.client_name}</div>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 12 }}>Email</Text>
                <div>{clientDetails.client_email}</div>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 12 }}>Status</Text>
                <div>
                  <Tag color={getStatusColor(clientDetails.client_status)}>{clientDetails.client_status}</Tag>
                </div>
              </Col>
            </Row>
          </div>

          {/* Suspend Tenant Button */}
          {selectedClient && clientDetails.client_status !== 'suspended' && clientDetails.client_status !== 'rejected' && (
            <Button
              danger
              icon={<StopOutlined />}
              onClick={() => handleSuspendTenant(selectedClient.id)}
              block
              style={{ marginTop: 4 }}
            >
              Suspend Tenant
            </Button>
          )}
          {clientDetails.client_status === 'suspended' && (
            <Alert
              type="warning"
              message="This tenant is suspended"
              description="All businesses and applications have been deactivated."
              showIcon
              style={{ marginTop: 4 }}
            />
          )}
        </Space>
      ) : null,
    },
    {
      key: 'businesses',
      label: (
        <Space>
          <KeyOutlined />
          Businesses
        </Space>
      ),
      children: (
        <div>
          <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
            <Title level={5} style={{ margin: 0 }}>Businesses</Title>
            <Space>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                loading={bizLoading}
                onClick={() => { if (selectedClient) loadBusinesses(selectedClient.id); }}
              >
                Refresh
              </Button>
              <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setCreateBizModalOpen(true)}>
                Create Business
              </Button>
            </Space>
          </Flex>

          {/* Pending business actions for this tenant */}
          {(() => {
            const tenantPendingBiz = pendingActions.filter(
              (a) => a.tenant_id === selectedClient?.id && (a.action_type === 'create_business' || a.action_type === 'delete_business')
            );
            return tenantPendingBiz.length > 0 ? (
              <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: 12 }}>
                {tenantPendingBiz.map((action) => (
                  <Card
                    key={`pending-biz-${action.id}`}
                    size="small"
                    style={{ borderStyle: 'dashed', borderColor: '#faad14', backgroundColor: '#fffbe6' }}
                  >
                    <Flex justify="space-between" align="center">
                      <Flex gap={8} align="center">
                        <ClockCircleOutlined style={{ color: '#faad14' }} />
                        <Tag color="warning">Pending Approval</Tag>
                        <Tag color={action.action_type === 'create_business' ? 'blue' : 'red'}>
                          {action.action_type === 'create_business' ? 'Create Business' : 'Delete Business'}
                        </Tag>
                        <Text strong>{action.payload?.name || action.business_name || 'Unknown'}</Text>
                      </Flex>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {action.submitted_at ? formatISTDate(action.submitted_at) : ''}
                      </Text>
                    </Flex>
                    {action.payload?.scopes && (
                      <Flex wrap="wrap" gap={4} style={{ marginTop: 6, marginLeft: 30 }}>
                        {action.payload.scopes.slice(0, 4).map((s: string) => (
                          <Tag key={s} style={{ fontSize: 10 }}>{s}</Tag>
                        ))}
                        {action.payload.scopes.length > 4 && <Tag style={{ fontSize: 10 }}>+{action.payload.scopes.length - 4} more</Tag>}
                      </Flex>
                    )}
                  </Card>
                ))}
              </Space>
            ) : null;
          })()}

          {bizLoading ? (
            <div style={{ textAlign: 'center', padding: 32 }}><Spin size="small" /></div>
          ) : businesses.length === 0 && pendingActions.filter((a) => a.tenant_id === selectedClient?.id && a.action_type === 'create_business').length === 0 ? (
            <Alert
              message='No businesses yet. Click "Create Business" to get started.'
              type="info"
              showIcon
            />
          ) : (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {businesses.map((biz) => (
                <Card
                  key={biz.id}
                  size="small"
                  hoverable
                  onClick={() => handleSelectBiz(biz)}
                  style={{ cursor: 'pointer', borderColor: selectedBiz?.id === biz.id ? '#1677ff' : undefined }}
                >
                  <Flex justify="space-between" align="flex-start">
                    <div>
                      <Flex align="center" gap={8}>
                        <Text strong>{biz.name}</Text>
                        <span style={idStyle('BIZ', 12)}>{fmtId('BIZ', biz.id)}</span>
                        <Tag color={biz.status === 'active' ? 'success' : 'default'}>{biz.status}</Tag>
                        <Tooltip title="Edit Business">
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={(e) => openEditBizModal(biz, e)}
                          />
                        </Tooltip>
                      </Flex>
                      {biz.description && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>{biz.description}</Text>
                      )}
                      <Flex wrap="wrap" gap={4} style={{ marginTop: 8 }}>
                        {(biz.scopes || []).slice(0, 5).map((scope: string) => (
                          <Tag key={scope} style={{ fontSize: 11 }}>{scope}</Tag>
                        ))}
                        {(biz.scopes || []).length > 5 && (
                          <Tag style={{ fontSize: 11 }}>+{(biz.scopes || []).length - 5} more</Tag>
                        )}
                      </Flex>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Rate: {biz.rate_limit_per_hour || 1000}/hr</Text>
                      <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Applications: {biz.application_count || 0}</Text>
                    </div>
                  </Flex>

                  {/* Show applications when business is selected */}
                  {selectedBiz?.id === biz.id && (
                    <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }} onClick={(e) => e.stopPropagation()}>

                      {/* Application (OAuth) Section */}
                      <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
                        <Text strong style={{ fontSize: 13 }}>Applications (OAuth)</Text>
                        <Button
                          size="small"
                          type="primary"
                          icon={<PlusOutlined />}
                          loading={createOAuthLoading}
                          onClick={handleCreateOAuthClient}
                        >
                          Add Application
                        </Button>
                      </Flex>

                      {oauthClientsLoading ? (
                        <Spin size="small" />
                      ) : oauthClients.length === 0 ? (
                        <Text type="secondary" style={{ fontSize: 12 }}>No applications yet. Add one to get API credentials.</Text>
                      ) : (
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          {oauthClients.map((oc: any) => (
                            <Card key={oc.id || oc.client_id} size="small" style={{ backgroundColor: '#f0f5ff', borderColor: '#adc6ff' }}>
                              <Flex justify="space-between" align="center">
                                <div>
                                  <Flex align="center" gap={8}>
                                    <SafetyOutlined style={{ color: '#1677ff' }} />
                                    <Text strong style={{ fontFamily: 'monospace', fontSize: 12 }}>{oc.client_id}</Text>
                                    <Tag color={oc.is_active ? 'success' : 'error'} style={{ fontSize: 11 }}>
                                      {oc.is_active ? 'active' : 'revoked'}
                                    </Tag>
                                    {oc.tier && <Tag style={{ fontSize: 10 }}>{oc.tier}</Tag>}
                                  </Flex>
                                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
                                    Secret: {oc.client_secret_prefix || '***'}... | Created: {oc.created_at ? formatISTDate(oc.created_at) : 'N/A'}
                                  </Text>
                                </div>
                                <Space size="small">
                                  <Tooltip title="Copy Client ID">
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<CopyOutlined />}
                                      onClick={(e) => { e.stopPropagation(); copyToClipboard(oc.client_id); }}
                                    />
                                  </Tooltip>
                                  <Tooltip title="Rotate Secret">
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<ReloadOutlined />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        Modal.confirm({
                                          title: 'Rotate Client Secret?',
                                          content: 'The current secret will be invalidated immediately. All tokens issued with the old secret will continue to work until they expire.',
                                          okText: 'Rotate',
                                          okType: 'danger',
                                          onOk: () => handleRotateSecret(oc.client_id),
                                        });
                                      }}
                                    />
                                  </Tooltip>
                                  <Tooltip title="Revoke Application">
                                    <Button
                                      type="text"
                                      size="small"
                                      danger
                                      icon={<DeleteOutlined />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        Modal.confirm({
                                          title: 'Revoke Application?',
                                          content: 'This will permanently revoke this application. It cannot be undone.',
                                          okText: 'Revoke',
                                          okType: 'danger',
                                          onOk: () => handleRevokeOAuthClient(oc.client_id),
                                        });
                                      }}
                                    />
                                  </Tooltip>
                                </Space>
                              </Flex>
                            </Card>
                          ))}
                        </Space>
                      )}

                    </div>
                  )}
                </Card>
              ))}
            </Space>
          )}
        </div>
      ),
    },
    {
      key: 'history',
      label: (
        <Space>
          <HistoryOutlined />
          History
        </Space>
      ),
      children: (
        <div>
          <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
            <Text strong>Activity Log</Text>
            <Button
              size="small"
              type="text"
              icon={<ReloadOutlined />}
              loading={detailsLoading}
              onClick={() => { if (selectedClient) loadClientDetails(selectedClient.id); }}
            />
          </Flex>
          {auditLogs.length === 0 ? (
            <Alert message="No activity history found for this tenant" type="info" showIcon />
          ) : (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {auditLogs.map((log) => {
              const colorMap: Record<string, string> = {
                success: 'success',
                failure: 'error',
                pending: 'warning',
              };
              const eventLabel = (log.event_type || log.action || '').replace(/_/g, ' ');
              const details = typeof log.request_details === 'string'
                ? (() => { try { return JSON.parse(log.request_details); } catch { return null; } })()
                : log.request_details;
              const changes: Record<string, { from: any; to: any }> = details?.changes || {};
              const hasChanges = Object.keys(changes).length > 0;
              const targetName = details?.application_name || details?.business_name || null;
              const bizIdLabel = fmtId('BIZ', details?.business_id);
              const appIdLabel = fmtId('APP', details?.application_id);
              const fieldLabels: Record<string, string> = {
                status: 'Status',
                name: 'Name',
                description: 'Description',
                key_type: 'Key Type',
                rate_limit_per_hour: 'Rate Limit/Hour',
                rate_limit_per_minute: 'Rate Limit/Min',
                daily_quota: 'Daily Quota',
                monthly_quota: 'Monthly Quota',
                scopes: 'Scopes',
                allowed_origins: 'Allowed Origins',
                allowed_ips: 'Allowed IPs',
                username: 'Application Name',
              };
              return (
                <Card key={log.id} size="small" bodyStyle={{ padding: '10px 14px' }}>
                  <Flex justify="space-between" align="flex-start" style={{ marginBottom: 4 }}>
                    <Space size={6} wrap>
                      <Tag color={colorMap[log.status] || 'default'} style={{ fontSize: 11, textTransform: 'capitalize' }}>
                        {log.status}
                      </Tag>
                      <Text strong style={{ fontSize: 13, textTransform: 'capitalize' }}>{eventLabel}</Text>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                      {log.created_at ? formatIST(log.created_at) : 'N/A'}
                    </Text>
                  </Flex>
                  <Flex gap={8} wrap="wrap" style={{ fontSize: 12 }}>
                    {log.username && <Text type="secondary">by <Text strong style={{ fontSize: 12 }}>{log.username}</Text></Text>}
                    {bizIdLabel && <Text type="secondary">| <span style={idStyle('BIZ', 11)}>{bizIdLabel}</span></Text>}
                    {appIdLabel && <Text type="secondary">| <span style={idStyle('APP', 11)}>{appIdLabel}</span></Text>}
                    {targetName && <Text type="secondary">| <Text strong style={{ fontSize: 12 }}>{targetName}</Text></Text>}
                    {log.ip_address && <Text type="secondary">| IP: {log.ip_address}</Text>}
                  </Flex>
                  {log.error_message && (
                    <Text type="danger" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>{log.error_message}</Text>
                  )}
                  {hasChanges && (
                    <div style={{ marginTop: 6, background: '#f6f8fa', padding: '6px 10px', borderRadius: 4, fontSize: 12 }}>
                      {Object.entries(changes).map(([field, change]) => {
                        const label = fieldLabels[field] || field.replace(/_/g, ' ');
                        const fromVal = Array.isArray(change.from) ? change.from.join(', ') : String(change.from ?? '—');
                        const toVal = Array.isArray(change.to) ? change.to.join(', ') : String(change.to ?? '—');
                        return (
                          <div key={field} style={{ marginBottom: 2 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>{label}: </Text>
                            <Text delete style={{ fontSize: 12, color: '#cf1322' }}>{fromVal}</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}> → </Text>
                            <Text strong style={{ fontSize: 12, color: '#389e0d' }}>{toVal}</Text>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {details && !hasChanges && (details.business_name || details.application_name || details.scopes) && (
                    <div style={{ marginTop: 6, background: '#f6f8fa', padding: '6px 10px', borderRadius: 4, fontSize: 12 }}>
                      {details.business_name && <div><Text type="secondary" style={{ fontSize: 12 }}>Business: </Text><Text strong style={{ fontSize: 12 }}>{details.business_name}</Text>{bizIdLabel && <Text code style={{ fontSize: 11, marginLeft: 6 }}>{bizIdLabel}</Text>}</div>}
                      {details.application_name && <div><Text type="secondary" style={{ fontSize: 12 }}>Application: </Text><Text strong style={{ fontSize: 12 }}>{details.application_name}</Text>{appIdLabel && <Text code style={{ fontSize: 11, marginLeft: 6 }}>{appIdLabel}</Text>}</div>}
                      {details.scopes && <div><Text type="secondary" style={{ fontSize: 12 }}>Scopes: </Text><Text style={{ fontSize: 12 }}>{Array.isArray(details.scopes) ? details.scopes.join(', ') : details.scopes}</Text></div>}
                      {details.key_type && <div><Text type="secondary" style={{ fontSize: 12 }}>Key Type: </Text><Text style={{ fontSize: 12 }}>{details.key_type}</Text></div>}
                    </div>
                  )}
                </Card>
              );
            })}
          </Space>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Error Alert */}
      {error && (
        <Alert message={error} type="error" closable onClose={() => setError('')} style={{ marginBottom: 24 }} showIcon />
      )}

      {/* Content */}
      {renderContent()}

      {/* Create Tenant Modal -- simplified: basic info only */}
      <Modal
        title="Create New Tenant"
        open={createOpen}
        onCancel={() => { setCreateOpen(false); clientForm.resetFields(); }}
        footer={[
          <Button key="cancel" onClick={() => { setCreateOpen(false); clientForm.resetFields(); }}>Cancel</Button>,
          <Button key="create" type="primary" loading={createLoading} onClick={handleCreateClient}>Create Tenant</Button>,
        ]}
      >
        <Alert
          type="info"
          showIcon
          message="Create the tenant first, then add Businesses and Applications in the tenant drawer."
          style={{ marginBottom: 16 }}
        />
        <Form form={clientForm} layout="vertical">
          <Form.Item name="name" label="Tenant Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} placeholder="Enter tenant name" />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Email is required' }, { type: 'email', message: 'Invalid email format' }]}>
            <Input prefix={<MailOutlined style={{ color: '#bfbfbf' }} />} placeholder="Enter email" />
          </Form.Item>
          <Form.Item name="primary_contact" label="Primary Contact">
            <Input prefix={<BankOutlined style={{ color: '#bfbfbf' }} />} placeholder="Enter primary contact name" />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input prefix={<PhoneOutlined style={{ color: '#bfbfbf' }} />} placeholder="Enter phone number" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Create Business Modal */}
      <Modal
        title={`Create Business for ${selectedClient?.name}`}
        open={createBizModalOpen}
        onCancel={() => { setCreateBizModalOpen(false); bizForm.resetFields(); }}
        footer={[
          <Button key="cancel" onClick={() => { setCreateBizModalOpen(false); bizForm.resetFields(); }}>Cancel</Button>,
          <Button key="create" type="primary" loading={createBizLoading} onClick={handleCreateBusiness}>Create Business</Button>,
        ]}
        width={600}
      >
        <Form form={bizForm} layout="vertical" initialValues={{ rate_limit_per_hour: 1000, rate_limit_per_minute: 60 }}>
          <Form.Item name="biz_name" label="Business Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input placeholder="e.g., Web Portal, Mobile App, Partner Integration" />
          </Form.Item>
          <Form.Item name="biz_description" label="Description">
            <Input.TextArea placeholder="What is this business used for?" rows={2} />
          </Form.Item>
          <Form.Item name="biz_scopes" label="Scopes" extra="Select API permissions for this business">
            <Select
              mode="multiple"
              placeholder="Select scopes..."
              options={scopeSelectOptions}
              optionRender={(option) => (
                <Space direction="vertical" size={0}>
                  <Text>{option.label}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>{(option.data as any).description}</Text>
                </Space>
              )}
            />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="rate_limit_per_hour" label="Rate Limit (per hour)">
                <Input type="number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="rate_limit_per_minute" label="Rate Limit (per minute)">
                <Input type="number" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="daily_quota" label="Daily Quota">
                <Input type="number" placeholder="Unlimited" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="monthly_quota" label="Monthly Quota">
                <Input type="number" placeholder="Unlimited" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Edit Business Modal */}
      <Modal
        title={`Edit Business: ${selectedBiz?.name}`}
        open={editBizModalOpen}
        onCancel={() => { setEditBizModalOpen(false); editBizForm.resetFields(); }}
        footer={[
          <Button key="cancel" onClick={() => { setEditBizModalOpen(false); editBizForm.resetFields(); }}>Cancel</Button>,
          <Button key="save" type="primary" loading={editBizLoading} onClick={handleEditBusiness}>Save Changes</Button>,
        ]}
        width={600}
      >
        <Form form={editBizForm} layout="vertical">
          <Form.Item name="biz_name" label="Business Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input placeholder="e.g., Web Portal, Mobile App, Partner Integration" />
          </Form.Item>
          <Form.Item name="biz_description" label="Description">
            <Input.TextArea placeholder="What is this business used for?" rows={2} />
          </Form.Item>
          <Form.Item name="biz_scopes" label="Scopes" extra="Select API permissions for this business">
            <Select
              mode="multiple"
              placeholder="Select scopes..."
              options={scopeSelectOptions}
              optionRender={(option) => (
                <Space direction="vertical" size={0}>
                  <Text>{option.label}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>{(option.data as any).description}</Text>
                </Space>
              )}
            />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="rate_limit_per_hour" label="Rate Limit (per hour)">
                <Input type="number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="rate_limit_per_minute" label="Rate Limit (per minute)">
                <Input type="number" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="daily_quota" label="Daily Quota">
                <Input type="number" placeholder="Unlimited" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="monthly_quota" label="Monthly Quota">
                <Input type="number" placeholder="Unlimited" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>


      {/* Tenant Details Drawer */}
      <Drawer
        title={
          <Flex align="center" gap={12}>
            <Avatar size={48} style={{ backgroundColor: '#1677ff', fontSize: '1.25rem' }}>
              {selectedClient?.name?.[0]?.toUpperCase()}
            </Avatar>
            <div style={{ flex: 1 }}>
              <Flex align="center" gap={8}>
                <Text strong style={{ fontSize: 16 }}>
                  {selectedClient?.name}
                </Text>
                {selectedClient && (
                  <span style={idStyle('TNT', 12)}>{fmtId('TNT', selectedClient.id)}</span>
                )}
              </Flex>
              <Text type="secondary">{selectedClient?.email}</Text>
            </div>
            {selectedClient && (
              <Tag color={getStatusColor(selectedClient.status)} icon={getStatusIcon(selectedClient.status)}>
                {selectedClient.status}
              </Tag>
            )}
          </Flex>
        }
        placement="right"
        width={isMobile ? '100%' : 600}
        open={drawerOpen}
        onClose={handleCloseDrawer}
      >
        <Tabs activeKey={drawerTab} onChange={setDrawerTab} items={drawerTabItems} />
      </Drawer>

    </div>
  );
}

export default ClientManagement;
